import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, Upload, GripVertical, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category options (must match backend MRSBUM_CATEGORIES ids)
const CATEGORIES = [
  { value: 'bracelets', label: 'Bracelets' },
  { value: 'anklets', label: 'Anklets' },
  { value: 'tie-dye', label: 'Tie Dye Apparel' },
  { value: 'baskets', label: 'Woven Baskets' }
];

// Default sizes for apparel
const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const JEWELRY_SIZES = ['Medium', 'Large'];

function AdminMrsBum({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const emptyProduct = {
    title: '',
    description: '',
    image_url: '',
    additional_images: [],
    category: 'bracelets',
    price: 0,
    sizes: [],
    colors: [],
    active: true,
    stock: null,
    one_of_a_kind: false
  };

  const [formData, setFormData] = useState(emptyProduct);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/mrsbum-products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('folder', 'mrsbum');

    try {
      const response = await axios.post('/upload/image', uploadData);
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('folder', 'mrsbum');

    try {
      const response = await axios.post('/upload/image', uploadData);
      setFormData(prev => ({
        ...prev,
        additional_images: [...(prev.additional_images || []), response.data.url]
      }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image_url || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    // A one-of-a-kind piece is always 1 in stock until it sells (0 = sold).
    const payload = formData.one_of_a_kind && formData.stock == null ? { ...formData, stock: 1 } : formData;

    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/mrsbum-products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/mrsbum-products`, payload);
        toast.success('Product created');
      }
      
      fetchProducts();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/mrsbum-products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      image_url: product.image_url,
      additional_images: product.additional_images || [],
      category: product.category || 'bracelets',
      price: product.price,
      sizes: product.sizes || [],
      colors: product.colors || [],
      active: product.active,
      stock: product.stock ?? null,
      one_of_a_kind: !!product.one_of_a_kind
    });
    setShowAddModal(true);
  };

  // Opens the Add form filled in from an existing product (for a batch of
  // similar one-of-a-kind pieces): change the title, size and photos, save.
  const handleDuplicate = (product) => {
    const { id, created_at, sort_order, ...rest } = product;
    setEditingProduct(null);
    setFormData({
      ...emptyProduct,
      ...rest,
      title: `${product.title} (copy)`,
      stock: product.one_of_a_kind ? 1 : (product.stock ?? null)
    });
    setShowAddModal(true);
  };

  const handleMarkSold = async (product, sold) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/mrsbum-products/${product.id}/sold`, { sold });
      toast.success(sold ? `${product.title} marked sold` : `${product.title} is available again`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingProduct(null);
    setFormData(emptyProduct);
  };

  const toggleSize = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) 
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const addColor = () => {
    const color = prompt('Enter color name:');
    if (color && !formData.colors.includes(color)) {
      setFormData(prev => ({
        ...prev,
        colors: [...prev.colors, color]
      }));
    }
  };

  const removeColor = (color) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== color)
    }));
  };

  const needsSizes = ['tie-dye', 'bracelets', 'anklets'].includes(formData.category);
  const activeSizeList = formData.category === 'tie-dye' ? DEFAULT_SIZES : JEWELRY_SIZES;

  return (
    <AdminLayout onLogout={onLogout} currentPage="mrsbum">
      <div className="admin-content">
        <div className="admin-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h1 style={{ 
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '28px',
              margin: 0
            }}>
              Mrs. Bum Products
            </h1>
            <p style={{ color: '#666', margin: '5px 0 0' }}>
              Manage tie dye apparel, bracelets, anklets, and woven baskets
            </p>
          </div>
          <Button 
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: '#7c3aed'
            }}
          >
            <Plus size={18} />
            Add Product
          </Button>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: '#f3e8ff',
            borderRadius: '12px'
          }}>
            <p style={{ color: '#6b21a8', marginBottom: '20px' }}>
              No products yet. Add your first Mrs. Bum product!
            </p>
            <Button 
              onClick={() => setShowAddModal(true)}
              style={{ background: '#7c3aed' }}
            >
              <Plus size={18} style={{ marginRight: '8px' }} />
              Add Product
            </Button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {products.map(product => (
              <div 
                key={product.id}
                style={{
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  opacity: product.active ? 1 : 0.6
                }}
              >
                <div style={{
                  aspectRatio: '1',
                  overflow: 'hidden',
                  background: '#f3e8ff'
                }}>
                  <img 
                    src={product.image_url} 
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ padding: '15px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>{product.title}</h3>
                    <span style={{
                      background: product.active ? '#dcfce7' : '#fee2e2',
                      color: product.active ? '#166534' : '#991b1b',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {product.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#888',
                    margin: '0 0 5px',
                    textTransform: 'capitalize'
                  }}>
                    {CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                  </p>
                  <p style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#7c3aed',
                    margin: '0 0 5px'
                  }}>
                    ${product.price}
                  </p>
                  <p style={{ fontSize: '13px', color: product.stock === 0 ? '#dc2626' : '#666', margin: '0 0 10px' }}>
                    {product.one_of_a_kind
                      ? (product.stock === 0 ? 'One of a kind · Sold' : 'One of a kind · Available')
                      : product.stock === null || product.stock === undefined ? 'Stock: Unlimited' : product.stock === 0 ? 'Out of Stock' : `Stock: ${product.stock}`}
                  </p>
                  {product.one_of_a_kind && (
                    <Button
                      size="sm"
                      variant={product.stock === 0 ? 'outline' : 'default'}
                      onClick={() => handleMarkSold(product, product.stock !== 0)}
                      style={{ width: '100%', marginBottom: '10px' }}
                    >
                      {product.stock === 0 ? 'Undo Sold' : 'Mark Sold'}
                    </Button>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(product)}
                      style={{ flex: 1 }}
                    >
                      <Edit2 size={14} style={{ marginRight: '5px' }} />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(product)}
                      style={{ flex: 1 }}
                    >
                      <Copy size={14} style={{ marginRight: '5px' }} />
                      Duplicate
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      style={{ color: '#dc2626', borderColor: '#dc2626' }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{ margin: 0, fontSize: '20px' }}>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>
                <button 
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                {/* Image Upload */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Product Image *
                  </label>
                  {formData.image_url ? (
                    <div style={{ position: 'relative', marginBottom: '10px' }}>
                      <img 
                        src={formData.image_url} 
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '150px',
                      border: '2px dashed #7c3aed',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: '#f3e8ff'
                    }}>
                      <Upload size={32} color="#7c3aed" />
                      <span style={{ marginTop: '10px', color: '#6b21a8' }}>
                        {uploading ? 'Uploading...' : 'Click to upload image'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>

                {/* Additional Images */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Additional Images
                  </label>
                  {(formData.additional_images || []).length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {formData.additional_images.map((url, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={url}
                            alt={`Additional ${idx + 1}`}
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeAdditionalImage(idx)}
                            style={{
                              position: 'absolute', top: '-8px', right: '-8px',
                              background: '#dc2626', color: '#fff', border: 'none',
                              borderRadius: '50%', width: '20px', height: '20px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', border: '1px dashed #7c3aed', borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer', background: '#f3e8ff', color: '#6b21a8', fontSize: '13px'
                  }}>
                    <Upload size={16} />
                    {uploading ? 'Uploading...' : 'Add image'}
                    <input type="file" accept="image/*" onChange={handleAdditionalImageUpload} style={{ display: 'none' }} disabled={uploading} />
                  </label>
                </div>

                {/* Title */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., Ocean Sunset Tie Dye Tee"
                  />
                </div>

                {/* Category */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      category: e.target.value,
                      sizes: []
                    }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Price *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666'
                    }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 25px',
                        border: '1px solid #d0d0d0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Sizes (for apparel only) */}
                {needsSizes && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontWeight: '500'
                    }}>
                      Available Sizes
                    </label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {activeSizeList.map(size => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => toggleSize(size)}
                          style={{
                            padding: '8px 14px',
                            border: formData.sizes.includes(size) ? '2px solid #7c3aed' : '1px solid #d0d0d0',
                            borderRadius: '6px',
                            background: formData.sizes.includes(size) ? '#f3e8ff' : '#fff',
                            color: formData.sizes.includes(size) ? '#6b21a8' : '#333',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Colors */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Available Colors
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {formData.colors.map(color => (
                      <span
                        key={color}
                        style={{
                          padding: '6px 12px',
                          background: '#f3e8ff',
                          borderRadius: '20px',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {color}
                        <button
                          type="button"
                          onClick={() => removeColor(color)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addColor}
                  >
                    <Plus size={14} style={{ marginRight: '5px' }} />
                    Add Color
                  </Button>
                </div>

                {/* Description */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                    placeholder="Product description..."
                  />
                </div>

                {/* One of a kind */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      id="one_of_a_kind"
                      checked={formData.one_of_a_kind}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        one_of_a_kind: e.target.checked,
                        stock: e.target.checked ? (prev.stock === 0 ? 0 : 1) : prev.stock
                      }))}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <label htmlFor="one_of_a_kind" style={{ fontWeight: '500' }}>
                      One of a kind
                    </label>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '6px 0 0 28px' }}>
                    A single piece: the shop shows "One of a kind," sells it once, then shows "Sold."
                  </p>
                </div>

                {/* Stock */}
                {!formData.one_of_a_kind && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Quantity in Stock
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock ?? ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                      placeholder="Leave blank for unlimited"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                )}

                {/* Active Toggle */}
                <div style={{
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="active" style={{ fontWeight: '500' }}>
                    Active (visible on store)
                  </label>
                </div>

                {/* Submit Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={closeModal}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    style={{ 
                      flex: 1,
                      background: '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Save size={18} />
                    {editingProduct ? 'Update' : 'Create'} Product
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminMrsBum;
